<script setup lang="ts">
  import * as z from 'zod';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { ParagraphProperties as ParagraphPropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Switch,
    Textarea,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    name: string;
    label?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
  };

  usePropertyForm<FormValues, ParagraphPropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      value: z.string().max(FORM_FIELD_CHARACTER_LIMITS.PARAGRAPH_VALUE).optional(),
      placeholder: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
      required: z.boolean().optional(),
    }),
    extractInitialValues: (properties: ParagraphPropsType | undefined): FormValues => ({
      name: properties?.name ?? '',
      label: properties?.label ?? '',
      value: properties?.value ?? '',
      placeholder: properties?.placeholder ?? '',
      required: properties?.required ?? false,
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: ParagraphPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        label: values.label || undefined,
        labelPosition: 'top',
        value: values.value || undefined,
        placeholder: values.placeholder || undefined,
        required: values.required ?? false,
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Parágrafo 01"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="value"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Valor
          </FormLabel>
          <FormControl>
            <Textarea
              v-bind="componentField"
              placeholder="Digite aqui o valor"
              class="min-h-[84px] resize-y"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.PARAGRAPH_VALUE"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-11 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
